/**
 * ALPINE.JS DEVELOPMENT DIRECTIVE
 *
 * Universal Principles (applies to all Alpine components and stores):
 *
 * 1. Unidirectional Data Flow: State changes should flow in one direction.
 *    User interaction → Update state → Render/reconcile UI
 *
 * 2. Explicit State Management: State should live in Alpine data/store properties,
 *    not scattered across DOM attributes or hidden in closure variables.
 *
 * 3. Separation of Concerns:
 *    - State updates (synchronous)
 *    - DOM manipulation (synchronous, derives from state)
 *    - Async operations (fetch, with proper guards)
 *
 * 4. Idempotent Operations: Functions that render/reconcile UI should be safe to call
 *    multiple times with the same state without unintended side effects.
 *
 * 5. Single Responsibility: Each function should have one clear purpose.
 *    Avoid functions that both update state AND fetch data AND manipulate DOM.
 *
 * URL-as-SSOT Pattern (applies to features with shareable/persistent state):
 *
 * For features requiring deep links, bookmarks, browser history, or server coordination
 * (e.g., collection filters, sort order, pagination):
 *
 * 1. URL Query Parameters as Single Source of Truth: Never read DOM state to determine
 *    application state. Always read from window.location.search or URLSearchParams.
 *
 * 2. URL-First Updates: User interaction → Update URL immediately → Reconcile all UI
 *    elements from new URL state → Fetch server data using URL params.
 *
 * 3. State Reconciliation: Any function that modifies URL params MUST call a reconciliation
 *    function to sync all DOM elements to match the new URL state.
 *
 * 4. No Direct DOM Reads for State: Event handlers must calculate state changes from the
 *    event target only, not by querying multiple DOM elements to derive state.
 *
 * 5. Idempotent Reconciliation: Reconciliation functions must produce identical DOM output
 *    when called multiple times with the same URL params.
 *
 * See: wwc-2025/REFACTOR-FILTERS.md for collection filters refactor plan.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('header', () => ({
    get breakpoint() {
      return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-tablet')) || 768;
    },
    searchOpen: window.innerWidth >= 768,
    menuOpen: false,

    init() {
      this.searchOpen = window.innerWidth >= this.breakpoint;
      window.addEventListener('resize', () => {
        this.searchOpen = window.innerWidth >= this.breakpoint;
      });
    }
  }));

  Alpine.store('cart', {
    itemCount: window.cartData?.item_count || 0,
    items: window.cartData?.items || [],
    total: window.cartData?.total_price || 0,
    pendingQuantities: {},
    processing: {},
    retryCount: {},
    maxRetries: 5,

    async addToCart(variantId, quantity = 1, properties = null) {
      if (!window.shoppingEnabled) return { error: true, message: 'Shopping is not available' };
      try {
        const payload = { id: variantId, quantity };
        if (properties && Object.keys(properties).length > 0) {
          payload.properties = properties;
        }
        await this._performCartOperation('/cart/add.js', payload);
        Alpine.store('cartDrawer').show();
        return { ok: true };
      } catch (error) {
        console.error('Cart add error:', error);
        return {
          error: true,
          status: error.status,
          description: error.description,
          message: error.message,
          errors: error.errors,
        };
      }
    },

    formatMoney(cents) {
      return new Intl.NumberFormat('en-IE', {
        currency: 'EUR',
        style: 'currency'
      }).format(cents / 100);
    },

    async _performCartOperation(endpoint, payload) {
      const response = await fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!response.ok) {
        let errorData = {};
        try { errorData = await response.json(); } catch (e) {}
        const error = new Error(errorData.message || `Cart operation failed: ${response.status}`);
        error.status = response.status;
        error.description = errorData.description;
        error.errors = errorData.errors;
        throw error;
      }

      await this.refreshCart();
      return response;
    },

    async refreshCart() {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.itemCount = cart.item_count || 0;
      this.total = cart.total_price || 0;
      this.items = cart.items || [];
    },

    async updateQuantity(key, quantity) {
      try {
        await this._performCartOperation('/cart/change.js', { id: key, quantity });
      } catch (error) {
        console.error('Cart update error:', error);
      }
    },

    getItemByVariantId(variantId) {
      return this.items.find(item => item.variant_id === variantId);
    },

    addOrUpdateQuantity(variantId, targetQuantity) {
      this.pendingQuantities[variantId] = targetQuantity;

      if (!this.processing[variantId]) {
        this._processQueue(variantId);
      }
    },

    async _processQueue(variantId) {
      this.processing[variantId] = true;
      this.retryCount[variantId] = 0;

      while (this.pendingQuantities[variantId] !== undefined) {
        const targetQuantity = this.pendingQuantities[variantId];
        delete this.pendingQuantities[variantId];

        try {
          const existingItem = this.getItemByVariantId(variantId);

          if (!existingItem && targetQuantity === 0) {
            continue;
          }

          const endpoint = existingItem ? '/cart/change.js' : '/cart/add.js';
          const payload = existingItem
            ? { id: existingItem.key, quantity: targetQuantity }
            : { id: variantId, quantity: targetQuantity };

          await this._performCartOperation(endpoint, payload);
          this.retryCount[variantId] = 0;
        } catch (error) {
          console.error('Cart operation error:', error);

          if (this.retryCount[variantId] < this.maxRetries) {
            this.retryCount[variantId]++;
            const delay = Math.min(1000 * Math.pow(2, this.retryCount[variantId] - 1), 8000);
            await new Promise(resolve => setTimeout(resolve, delay));
            this.pendingQuantities[variantId] = targetQuantity;
          } else {
            console.error(`Max retries reached for variant ${variantId}`);
            this.retryCount[variantId] = 0;
          }
        }
      }

      this.processing[variantId] = false;
      delete this.retryCount[variantId];
    },
  });

  Alpine.store('cartDrawer', {
    open: false,

    close() {
      this.open = false;
    },
    show() {
      this.open = true;
    }
  });

  Alpine.store('collectionFilters', {
    filterCache: null,
    urlChanged: false,
    fetchId: 0,

    init() {
      window.addEventListener('popstate', () => {
        this.urlChanged = !this.urlChanged;
        const urlParams = new URLSearchParams(window.location.search);
        this.reconcileFromURL();
        this.fetchAndUpdateProducts(urlParams.toString());
      });
    },

    handleCheckboxChange(event) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);
      const name = checkbox.name;
      const value = checkbox.value;

      if (checkbox.checked) {
        urlParams.append(name, value);
      } else {
        const values = urlParams.getAll(name).filter(v => v !== value);
        urlParams.delete(name);
        values.forEach(v => urlParams.append(name, v));
      }

      this.updateURLAndFetch(urlParams);
    },

    handleABVChange(event, minABV, maxABV) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);
      const abvContainer = checkbox.closest('.filter-group__content');
      const hiddenInputs = abvContainer.querySelectorAll('input[data-abv]');

      hiddenInputs.forEach(input => {
        const paramName = input.name;
        urlParams.delete(paramName);
      });

      if (checkbox.checked) {
        hiddenInputs.forEach(input => {
          const abv = parseFloat(input.dataset.abv);
          const paramName = input.name;
          const paramValue = input.value;

          if (abv >= minABV && abv <= maxABV) {
            urlParams.append(paramName, paramValue);
          }
        });
      }

      this.updateURLAndFetch(urlParams);
    },

    handlePriceChange(event, minPrice, maxPrice, minParamName, maxParamName) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.delete(minParamName);
      urlParams.delete(maxParamName);

      if (checkbox.checked) {
        urlParams.set(minParamName, minPrice);
        if (maxPrice !== null) {
          urlParams.set(maxParamName, maxPrice);
        }
      }

      this.updateURLAndFetch(urlParams);
    },

    async initFilters() {
      await this.cacheFullFilterStructure();
      this.reconcileFromURL();
      this.updateABVRangeAvailability(document);

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.toString()) {
        await this.fetchAndUpdateProducts(urlParams.toString());
      }
    },

    async cacheFullFilterStructure() {
      if (this.filterCache) return;

      const collectionSection = document.querySelector('.collection')?.closest('.shopify-section');
      const sectionId = collectionSection ? collectionSection.id.replace('shopify-section-', '') : null;

      if (!sectionId) return;

      const fullFilterUrl = `${window.location.pathname}?section_id=${sectionId}`;
      const response = await fetch(fullFilterUrl);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.filterCache = {
        groups: []
      };

      doc.querySelectorAll('.filter-group').forEach(filterGroup => {
        const label = filterGroup.querySelector('.filter-group__title span')?.textContent?.trim();
        const options = [];

        filterGroup.querySelectorAll('.filter-option[data-filter-value]').forEach(option => {
          options.push({
            value: option.dataset.filterValue,
            paramName: option.dataset.filterParam,
            html: option.outerHTML
          });
        });

        if (label && options.length > 0) {
          this.filterCache.groups.push({ label, options });
        }
      });
    },

    reconcileFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const checkboxes = document.querySelectorAll('#collection-filters input[type="checkbox"][name][value]');

      checkboxes.forEach(checkbox => {
        const urlValues = urlParams.getAll(checkbox.name);
        checkbox.checked = urlValues.includes(checkbox.value);
      });

      this.reconcileABVRadios(urlParams);
      this.reconcilePriceRadios(urlParams);

      this.urlChanged = !this.urlChanged;
    },

    reconcileABVRadios(urlParams) {
      document.querySelectorAll('[data-abv-range]').forEach(label => {
        const radio = label.querySelector('input[type="checkbox"]');
        if (!radio) return;

        const range = label.dataset.abvRange.split('-');
        const minABV = parseFloat(range[0]);
        const maxABV = parseFloat(range[1]);

        const abvContainer = label.closest('.filter-group__content');
        const hiddenInputs = abvContainer?.querySelectorAll('input[data-abv]') || [];

        const activeAbvValues = [];
        hiddenInputs.forEach(input => {
          const urlValues = urlParams.getAll(input.name);
          if (urlValues.includes(input.value)) {
            activeAbvValues.push(parseFloat(input.dataset.abv));
          }
        });

        const allInRange = activeAbvValues.length > 0 && activeAbvValues.every(abv => abv >= minABV && abv <= maxABV);
        radio.checked = allInRange;
      });
    },

    reconcilePriceRadios(urlParams) {
      document.querySelectorAll('[data-price-range]').forEach(label => {
        const radio = label.querySelector('input[type="checkbox"]');
        if (!radio) return;

        const range = label.dataset.priceRange;
        const minParamName = 'filter.v.price.gte';
        const maxParamName = 'filter.v.price.lte';

        const urlMin = urlParams.get(minParamName);
        const urlMax = urlParams.get(maxParamName);

        let isSelected = false;

        if (range === '75-') {
          isSelected = urlMin === '75' && !urlMax;
        } else {
          const [min, max] = range.split('-').map(v => v || null);
          isSelected = urlMin === min && urlMax === max;
        }

        radio.checked = isSelected;
      });
    },

    toggleFilter(paramName, value) {
      const urlParams = new URLSearchParams(window.location.search);
      const current = urlParams.getAll(paramName);
      if (current.includes(value)) {
        urlParams.delete(paramName);
        current.filter(v => v !== value).forEach(v => urlParams.append(paramName, v));
      } else {
        urlParams.append(paramName, value);
      }
      this.updateURLAndFetch(urlParams);
    },

    exclusiveToggle(paramName, value) {
      const urlParams = new URLSearchParams(window.location.search);
      const isActive = urlParams.getAll(paramName).includes(value);
      urlParams.delete(paramName);
      if (!isActive) {
        urlParams.set(paramName, value);
      }
      this.updateURLAndFetch(urlParams);
    },

    switchToggle(paramName, value) {
      const urlParams = new URLSearchParams(window.location.search);
      const isActive = urlParams.getAll(paramName).includes(value);

      const newParams = new URLSearchParams();
      const sortBy = urlParams.get('sort_by');
      if (sortBy) {
        newParams.set('sort_by', sortBy);
      }

      if (!isActive) {
        newParams.set(paramName, value);
      }

      this.updateURLAndFetch(newParams);
    },

    async updateURLAndFetch(urlParams) {
      urlParams.delete('page');
      const paramsString = urlParams.toString();
      const url = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
      history.pushState({ params: paramsString }, '', url);
      this.urlChanged = !this.urlChanged;

      this.reconcileFromURL();
      await this.fetchAndUpdateProducts(paramsString);
    },

    async fetchAndUpdateProducts(params) {
      const collectionSection = document.querySelector('.collection')?.closest('.shopify-section');
      const sectionId = collectionSection ? collectionSection.id.replace('shopify-section-', '') : null;

      if (!sectionId) return;

      const currentFetchId = ++this.fetchId;
      const fetchUrl = `${window.location.pathname}?section_id=${sectionId}&${params}`;
      const response = await fetch(fetchUrl);
      if (currentFetchId !== this.fetchId) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.updateProductGrid(doc);
      this.updateProductCount(doc);
      this.updatePagination(doc);
      await this.updateFilterAvailability(doc);
    },

    async updateFilterAvailability(docWithCurrentFilters) {
      const currentOptions = document.querySelectorAll('.filter-option[data-filter-value]');

      currentOptions.forEach(option => {
        const value = option.dataset.filterValue;
        const paramName = option.dataset.filterParam;

        const fetchedOption = docWithCurrentFilters.querySelector(
          `.filter-option[data-filter-value="${value}"][data-filter-param="${paramName}"]`
        );

        const isAvailable = fetchedOption && !fetchedOption.classList.contains('filter-option--unavailable');

        const checkbox = option.querySelector('input[type="checkbox"]');
        if (checkbox && !checkbox.checked) {
          checkbox.disabled = !isAvailable;
          option.classList.toggle('filter-option--unavailable', !isAvailable);
        }
      });

      this.updateABVRangeAvailability(docWithCurrentFilters);

      requestAnimationFrame(() => this.sortFilterOptions());
    },

    updateABVRangeAvailability(fetchedDoc) {
      const ranges = { '0-0': 0, '0.1-9.99': 0, '10-13': 0, '13.01-100': 0 };
      const fetchedHiddenInputs = fetchedDoc.querySelectorAll('input[data-abv][data-abv-count]');

      fetchedHiddenInputs.forEach(input => {
        const abv = parseFloat(input.dataset.abv);
        const count = parseInt(input.dataset.abvCount, 10);
        if (abv === 0) ranges['0-0'] += count;
        else if (abv < 10) ranges['0.1-9.99'] += count;
        else if (abv <= 13) ranges['10-13'] += count;
        else ranges['13.01-100'] += count;
      });

      const container = document.querySelector('[data-abv-range]')?.closest('.filter-group--yMargin');
      if (!container) return;

      Object.entries(ranges).forEach(([range, count]) => {
        const label = container.querySelector(`[data-abv-range="${range}"]`);
        if (!label) return;
        const checkbox = label.querySelector('input[type="checkbox"]');
        if (checkbox && !checkbox.checked) {
          checkbox.disabled = count === 0;
          label.classList.toggle('filter-option--unavailable', count === 0);
        }
      });

      const rangeOrder = ['0-0', '0.1-9.99', '10-13', '13.01-100'];
      const labels = Array.from(container.querySelectorAll('[data-abv-range]'));
      labels.sort((a, b) => {
        const aDisabled = a.classList.contains('filter-option--unavailable');
        const bDisabled = b.classList.contains('filter-option--unavailable');
        if (aDisabled && !bDisabled) return 1;
        if (!aDisabled && bDisabled) return -1;
        return rangeOrder.indexOf(a.dataset.abvRange) - rangeOrder.indexOf(b.dataset.abvRange);
      });
      const fieldset = container.querySelector('fieldset');
      labels.forEach(label => container.insertBefore(label, fieldset));
    },

    sortFilterOptions() {
      document.querySelectorAll('.filter-group__content').forEach(container => {
        const options = Array.from(container.querySelectorAll('.filter-option[data-filter-value]'));

        if (options.length === 0) return;

        options.sort((a, b) => {
          const aDisabled = a.classList.contains('filter-option--unavailable');
          const bDisabled = b.classList.contains('filter-option--unavailable');

          if (aDisabled && !bDisabled) return 1;
          if (!aDisabled && bDisabled) return -1;

          const aValue = a.dataset.filterValue || '';
          const bValue = b.dataset.filterValue || '';

          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);

          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }

          const aLabel = a.querySelector('span')?.textContent?.trim() || aValue;
          const bLabel = b.querySelector('span')?.textContent?.trim() || bValue;
          return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
        });

        options.forEach(option => container.appendChild(option));
      });
    },

    updateProductGrid(doc) {
      const newProducts = doc.querySelector('.collection-products');
      const currentProducts = document.querySelector('.collection-products');
      if (newProducts && currentProducts) {
        currentProducts.innerHTML = newProducts.innerHTML;
      }
    },

    updateProductCount(doc) {
      const newCount = doc.querySelector('.collection-product-count');
      const currentCount = document.querySelector('.collection-product-count');
      if (newCount && currentCount) {
        const textNode = Array.from(currentCount.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        const newTextNode = Array.from(newCount.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode && newTextNode) {
          textNode.textContent = newTextNode.textContent;
        }
      }
    },

    updatePagination(doc) {
      const newPagination = doc.querySelector('.pagination');
      const currentPagination = document.querySelector('.pagination');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (!newPagination && currentPagination) {
        currentPagination.remove();
      } else if (newPagination && !currentPagination) {
        const grid = document.querySelector('.collection-products');
        if (grid) grid.after(newPagination);
      }
    },

    applySort(sortValue) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('sort_by', sortValue);

      this.updateURLAndFetch(urlParams);
    },

    clearAll() {
      const urlParams = new URLSearchParams(window.location.search);
      const sortBy = urlParams.get('sort_by');

      const newParams = new URLSearchParams();
      if (sortBy) {
        newParams.set('sort_by', sortBy);
      }

      this.updateURLAndFetch(newParams);
    },

    clearFilterGroup(filterGroupElement) {
      const urlParams = new URLSearchParams(window.location.search);
      const checkboxes = filterGroupElement.querySelectorAll('input[type="checkbox"][name]');

      checkboxes.forEach(checkbox => {
        if (checkbox.name) {
          urlParams.delete(checkbox.name);
        }
      });

      const hiddenInputs = filterGroupElement.querySelectorAll('input[type="hidden"][name]');
      hiddenInputs.forEach(input => {
        urlParams.delete(input.name);
      });

      filterGroupElement.querySelectorAll('[data-abv-range] input[type="checkbox"], [data-price-range] input[type="checkbox"]').forEach(cb => {
        cb.checked = false;
      });

      this.updateURLAndFetch(urlParams);
    },

    isFilterGroupActive(filterGroupElement) {
      this.urlChanged;
      const urlParams = new URLSearchParams(window.location.search);

      const rangeLabels = filterGroupElement.querySelectorAll('[data-abv-range], [data-price-range]');
      if (rangeLabels.length > 0) {
        return Array.from(rangeLabels).some(label => {
          const checkbox = label.querySelector('input[type="checkbox"]');
          return checkbox?.checked;
        });
      }

      const checkboxes = filterGroupElement.querySelectorAll('input[type="checkbox"][name]');
      return Array.from(checkboxes).some(checkbox => {
        const urlValues = urlParams.getAll(checkbox.name);
        return urlValues.includes(checkbox.value);
      });
    },

    hasAnyFilters() {
      this.urlChanged;
      const urlParams = new URLSearchParams(window.location.search);

      for (const [key] of urlParams) {
        if (key.startsWith('filter.')) {
          return true;
        }
      }

      return false;
    },




  });

  Alpine.data('productRecommendations', (productId) => ({
    hasProducts: false,
    loading: true,

    async init() {
      try {
        const recommendationsUrl = `/recommendations/products?section_id=product-recommendations&product_id=${productId}&limit=4`;
        const response = await fetch(recommendationsUrl);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const cards = doc.querySelectorAll('.product-card');

        if (cards.length > 0) {
          this.hasProducts = true;
          const grid = this.$el.querySelector('.product-recommendations__grid');

          if (grid) {
            cards.forEach(card => grid.appendChild(card));
          }
        }
      } catch (error) {
        console.error('Failed to load recommendations:', error);
      } finally {
        this.loading = false;
      }
    }
  }));

  Alpine.data('tasteStylePicker', (paramName, red, white, colourParamName, redColourValue, whiteColourValue) => ({
    colour: null,
    activeStyle: null,
    paramName,
    red,
    white,
    colourParamName,
    colourValues: { red: redColourValue, white: whiteColourValue },

    get styles() {
      if (this.colour === 'red') return this.red;
      if (this.colour === 'white') return this.white;
      return [];
    },

    syncFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const activeColour = urlParams.get(this.colourParamName);
      if (activeColour === this.colourValues.red) this.colour = 'red';
      else if (activeColour === this.colourValues.white) this.colour = 'white';
      else this.colour = null;

      const activeStyle = urlParams.get(this.paramName);
      this.activeStyle = activeStyle || null;
    },

    init() {
      this.syncFromURL();
      const picker = this;
      Alpine.effect(() => {
        void picker.$store.collectionFilters.urlChanged;
        picker.syncFromURL();
      });
    },

    selectColour(c) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(this.paramName);

      if (this.colour === c) {
        this.colour = null;
        this.activeStyle = null;
        urlParams.delete(this.colourParamName);
      } else {
        this.colour = c;
        this.activeStyle = null;
        urlParams.delete(this.colourParamName);
        urlParams.set(this.colourParamName, this.colourValues[c]);
      }

      this.$store.collectionFilters.updateURLAndFetch(urlParams);
    },

    selectStyle(style) {
      if (this.activeStyle === style.value) {
        this.activeStyle = null;
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete(this.paramName);
        this.$store.collectionFilters.updateURLAndFetch(urlParams);
      } else {
        this.activeStyle = style.value;
        this.$store.collectionFilters.exclusiveToggle(this.paramName, style.value);
      }
    }
  }));

  Alpine.data('foodPairingPicker', (paramName, items) => ({
    category: null,
    activePairing: null,
    paramName,
    items,

    categories: [
      { key: 'meat', label: 'Meat & Game' },
      { key: 'fish', label: 'Fish & Seafood' },
      { key: 'cheese', label: 'Cheese' },
      { key: 'italian', label: 'Italian' },
      { key: 'world', label: 'World Cuisine' },
      { key: 'veggie', label: 'Veggie' },
      { key: 'social', label: 'Light & Social' },
      { key: 'dessert', label: 'Dessert' },
    ],

    get availableCategories() {
      return this.categories.filter(c => this.items.some(i => i.cat === c.key));
    },

    get currentItems() {
      return this.items.filter(i => i.cat === this.category);
    },

    syncFromURL() {
      const activeValues = new URLSearchParams(window.location.search).getAll(this.paramName);
      if (activeValues.length === 1) {
        this.activePairing = activeValues[0];
        const match = this.items.find(i => i.value === activeValues[0]);
        if (match) this.category = match.cat;
      } else if (activeValues.length > 1) {
        this.activePairing = null;
        const match = this.items.find(i => i.value === activeValues[0]);
        if (match) this.category = match.cat;
      } else {
        this.activePairing = null;
        this.category = null;
      }
    },

    init() {
      this.syncFromURL();
      const picker = this;
      Alpine.effect(() => {
        void picker.$store.collectionFilters.urlChanged;
        picker.syncFromURL();
      });
    },

    selectCategory(cat) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(this.paramName);

      if (this.category === cat) {
        this.category = null;
        this.activePairing = null;
      } else {
        this.category = cat;
        this.activePairing = null;
        const catItems = this.items.filter(i => i.cat === cat);
        catItems.forEach(i => urlParams.append(this.paramName, i.value));
      }

      this.$store.collectionFilters.updateURLAndFetch(urlParams);
    },

    selectPairing(item) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(this.paramName);

      if (this.activePairing === item.value) {
        this.activePairing = null;
        const catItems = this.items.filter(i => i.cat === this.category);
        catItems.forEach(i => urlParams.append(this.paramName, i.value));
      } else {
        this.activePairing = item.value;
        urlParams.append(this.paramName, item.value);
      }

      this.$store.collectionFilters.updateURLAndFetch(urlParams);
    }
  }));
});

document.addEventListener('submit', async function (event) {
  const form = event.target;
  const container = form.closest('[data-newsletter-form]');
  if (!container) return;
  if (!form.checkValidity()) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const emailInput = form.querySelector('input[type="email"]');
  const email = emailInput.value.trim();
  const button = form.querySelector('button[type="submit"]');
  const originalLabel = button ? button.textContent : 'Subscribe';
  if (button) {
    button.disabled = true;
    button.textContent = 'Sending...';
  }

  try {
    const response = await fetch('/api/unstable/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': '60ad7c55fc07c65a619b582401038e2f',
      },
      body: JSON.stringify({
        query: 'mutation subscribe($email: String!) { customerEmailMarketingSubscribe(email: $email) { customerUserErrors { code message } } }',
        variables: { email },
      }),
    });

    const data = await response.json();
    const errors = data.data?.customerEmailMarketingSubscribe?.customerUserErrors;

    if (errors?.length > 0) {
      let errorDiv = container.querySelector('.newsletter__error');
      if (!errorDiv) {
        errorDiv = document.createElement('div');
        errorDiv.className = 'newsletter__error';
        const formFields = container.querySelector('.newsletter__form-fields');
        if (formFields) formFields.before(errorDiv);
      }
      errorDiv.textContent = errors[0].message;
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    } else {
      const title = container.querySelector('.newsletter__title')?.textContent || 'Newsletter Sign-Up';
      container.innerHTML =
        '<h2 class="newsletter__title">' + title + '</h2>' +
        '<div class="newsletter__success"><p>Thank you for subscribing</p></div>';
    }
  } catch (_) {
    let errorDiv = container.querySelector('.newsletter__error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'newsletter__error';
      const formFields = container.querySelector('.newsletter__form-fields');
      if (formFields) formFields.before(errorDiv);
    }
    errorDiv.textContent = 'Something went wrong. Please try again.';
    if (button) {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }
});

